import { Drawer } from "@mui/material";
import { BrandKit } from "@redotech/redo-content-builder-common/brand-kit";
import {
  DesktopStepConfig,
  DesktopStepType,
  MobileStepConfig,
  MobileStepType,
  SpotlightForm,
} from "@redotech/redo-spotlight-common/spotlight-schema";
import {
  getContainerStyles,
  SpotlightContainerStyles,
} from "@redotech/redo-spotlight-common/spotlight-styles";
import {
  MerchantCustomizableModal,
  ModalPlacement,
} from "@redotech/redo-web/storefront/merchant-customizable-modal";
import { ANONYMOUS_SHOPPER_ID_KEY } from "@redotech/redo-web/utils/shared-conf";
import { memo } from "react";
import { CustomFieldProvider } from "../form-elements/context";
import { getDrawerPaperPositionStyles } from "./drawer-position";
import { SpotlightFormContent } from "./spotlight-form-content";
import { SpotlightClient } from "./types";

export interface SpotlightModalProps {
  ariaLabel?: string;
  spotlight: SpotlightForm;
  open: boolean;
  completed: boolean;
  currentStep: number;
  onClose: () => void;
  client: SpotlightClient | undefined;
  brandKit?: BrandKit;
  isMobile: boolean;
  mobileConfig?: MobileStepConfig;
  desktopConfig?: DesktopStepConfig;
  merchantPhoneNumber?: string;
  onSubmitStep: (
    i: number,
    formData: Record<string, string>,
  ) => Promise<{ resolvedDiscounts?: Record<string, string> } | void>;
  resolvedDiscounts?: Record<string, string>;
  beforePossibleCheckoutNavigation?: () => Promise<void>;
  buttonSubtext?: string;
}

export const SpotlightModal = memo(function SpotlightModal({
  ariaLabel,
  spotlight,
  open,
  completed,
  currentStep,
  onClose,
  client,
  brandKit,
  isMobile,
  mobileConfig,
  desktopConfig,
  merchantPhoneNumber,
  onSubmitStep,
  resolvedDiscounts,
  beforePossibleCheckoutNavigation,
  buttonSubtext,
}: SpotlightModalProps) {
  const config = isMobile ? mobileConfig : desktopConfig;

  if (!config) {
    return null;
  }

  const allowBackgroundInteraction = spotlight.allowBackgroundInteraction;

  const isFullscreen =
    (isMobile && mobileConfig?.type === MobileStepType.FULLSCREEN) ||
    (!isMobile && desktopConfig?.type === DesktopStepType.FULLSCREEN);

  const isDrawer = isMobile && mobileConfig?.type === MobileStepType.DRAWER;
  const isPanel = !isMobile && desktopConfig?.type === DesktopStepType.PANEL;

  const content = (
    <CustomFieldProvider
      getCustomFieldOptions={(id) =>
        client?.getCustomFieldOptions({ customFieldId: id }) ??
        Promise.resolve({ name: "", options: [] })
      }
    >
      <SpotlightFormContent
        anonymousId={
          localStorage.getItem(ANONYMOUS_SHOPPER_ID_KEY) ?? undefined
        }
        beforePossibleCheckoutNavigation={beforePossibleCheckoutNavigation}
        brandKit={brandKit}
        buttonSubtext={buttonSubtext}
        config={config}
        currentStep={currentStep}
        isMobile={isMobile}
        merchantPhoneNumber={merchantPhoneNumber}
        onClose={onClose}
        onSubmitStep={onSubmitStep}
        resolvedDiscounts={resolvedDiscounts}
        spotlight={spotlight}
      />
    </CustomFieldProvider>
  );

  // Get overlay backdrop color from config
  const backdropColor =
    "overlay" in config.advancedSettings && config.advancedSettings.overlay
      ? config.advancedSettings.overlay
      : "rgba(0, 0, 0, 0.5)";

  // Overlay pass-through: pointer-events off on the Modal root (re-enabled on the
  // Paper) plus no body scroll lock, so the page behind stays interactive.
  const passThroughModalProps = allowBackgroundInteraction
    ? {
        disableScrollLock: true,
        disableEnforceFocus: true,
        style: { pointerEvents: "none" as const },
      }
    : undefined;
  const paperPassThroughStyle = allowBackgroundInteraction
    ? { pointerEvents: "auto" as const }
    : undefined;

  // Drawer for mobile drawer type - always anchored to bottom
  if (isDrawer && mobileConfig?.type === MobileStepType.DRAWER) {
    const containerStyles = getContainerStyles(
      mobileConfig,
      true,
      window.innerHeight,
    );
    return (
      <Drawer
        anchor="bottom"
        ModalProps={passThroughModalProps}
        onClose={onClose}
        open={open && !completed}
        PaperProps={{
          style: {
            ...containerStylesToCSS(containerStyles),
            ...getDrawerPaperPositionStyles("bottom"),
            ...paperPassThroughStyle,
          },
        }}
        slotProps={{ backdrop: { style: { backgroundColor: backdropColor } } }}
      >
        {content}
      </Drawer>
    );
  }

  // Panel for desktop panel type
  if (isPanel && desktopConfig?.type === DesktopStepType.PANEL) {
    const anchor = getDrawerAnchor(desktopConfig.placement);
    const containerStyles = getContainerStyles(
      desktopConfig,
      false,
      window.innerHeight,
    );
    return (
      <Drawer
        anchor={anchor}
        ModalProps={passThroughModalProps}
        onClose={onClose}
        open={open && !completed}
        PaperProps={{
          style: {
            ...containerStylesToCSS(containerStyles),
            ...getDrawerPaperPositionStyles(anchor),
            ...paperPassThroughStyle,
          },
        }}
        slotProps={{ backdrop: { style: { backgroundColor: backdropColor } } }}
      >
        {content}
      </Drawer>
    );
  }

  // Dialog for popup and fullscreen

  const placement: ModalPlacement =
    !isMobile && desktopConfig?.type === DesktopStepType.POPUP
      ? (desktopConfig.placement as ModalPlacement)
      : "center";

  // Don't pass containerStyle for fullscreen - let MUI Dialog handle it natively
  const containerStyles = isFullscreen
    ? undefined
    : containerStylesToCSS(
        getContainerStyles(config, isMobile, window.innerHeight),
      );

  return (
    <MerchantCustomizableModal
      allowBackgroundInteraction={allowBackgroundInteraction}
      ariaLabel={ariaLabel}
      backdropColor={backdropColor}
      containerStyle={containerStyles}
      fullscreen={isFullscreen}
      onClose={onClose}
      open={open && !completed}
      placement={placement}
    >
      {content}
    </MerchantCustomizableModal>
  );
});

function getDrawerAnchor(
  placement: string,
): "left" | "right" | "top" | "bottom" {
  switch (placement) {
    case "left":
      return "left";
    case "right":
      return "right";
    case "top":
      return "top";
    case "bottom":
      return "bottom";
    default:
      return "bottom";
  }
}

function containerStylesToCSS(
  styles: SpotlightContainerStyles,
): React.CSSProperties {
  return {
    width: styles.width,
    height: styles.height,
    minHeight: styles.minHeight,
    maxHeight: styles.maxHeight,
    borderRadius: styles.borderRadius,
    borderWidth: styles.borderWidth,
    borderColor: styles.borderColor,
    borderStyle: styles.borderWidth ? "solid" : undefined,
    backgroundColor: styles.backgroundColor,
  };
}
